// Startseite im Gezeiten-Design: Live-Suchvorschau, Gezeitenkurve der
// kommenden Sitzungen, Abfahrtstafel, neueste Dokumente und Abdeckung.
// Vollständig DOM-basiert, kein innerHTML.

import {
  el,
  documentUrl,
  preview,
  fileLabel,
  formatDate,
  relativeDay,
  fetchHome,
  searchPreview,
  TOPICS,
  contextualDocumentTitle,
} from "./home-shared.js";

const SVG_NS = "http://www.w3.org/2000/svg";

// ── Live-Suchvorschau ───────────────────────────────────────────────
const input = document.getElementById("hero-search-input");
const previewBox = document.getElementById("hero-preview");
let debounceTimer = null;

function renderSearchPreview(query, data) {
  if (!previewBox) return;
  if (!data.hits.length) {
    previewBox.replaceChildren(
      el("p", {
        class: "hero-preview-empty",
        text: `Keine Treffer für „${query}“.`,
      })
    );
    previewBox.hidden = false;
    return;
  }

  previewBox.replaceChildren(
    el(
      "ul",
      { class: "hero-preview-list" },
      data.hits.map((hit) =>
        el("li", {}, [
          el(
            "a",
            { class: "hero-preview-item", href: documentUrl(hit, query) },
            [
              el("span", { class: "hero-preview-media" }, [
                preview(hit, {
                  imgClass: "hero-preview-thumb",
                  fallbackClass: "hero-preview-fallback",
                }),
              ]),
              el("span", { class: "hero-preview-body" }, [
                el("span", {
                  class: "hero-preview-title",
                  text: contextualDocumentTitle(hit),
                }),
                el("span", {
                  class: "hero-preview-meta",
                  text: [
                    hit.Gremium,
                    hit.Kommune,
                    formatDate(hit.start),
                    fileLabel(hit),
                  ]
                    .filter(Boolean)
                    .join(" · "),
                }),
                // KI-Zusammenfassung nur zeigen, wenn vorhanden.
                hit.summary &&
                  el("span", {
                    class: "hero-preview-summary",
                    text: hit.summary,
                  }),
              ]),
            ]
          ),
        ])
      )
    ),
    el("a", {
      class: "hero-preview-all",
      href: `/suche?q=${encodeURIComponent(query)}`,
      text: `Alle ${data.found.toLocaleString("de-DE")} Treffer anzeigen`,
    })
  );
  previewBox.hidden = false;
}

function handleSearchInput() {
  if (!input || !previewBox) return;
  const query = input.value.trim();
  clearTimeout(debounceTimer);
  if (query.length < 2) {
    previewBox.hidden = true;
    return;
  }
  debounceTimer = setTimeout(async () => {
    try {
      const data = await searchPreview(query, 5);
      // Nutzer hat weitergetippt -> veraltetes Ergebnis verwerfen.
      if (input.value.trim() !== query) return;
      renderSearchPreview(query, data);
    } catch {
      previewBox.hidden = true;
    }
  }, 220);
}

function setupSearch() {
  if (!input || !previewBox) return;
  input.addEventListener("input", handleSearchInput);
  input.addEventListener("focus", handleSearchInput);
  document.addEventListener("click", (e) => {
    if (previewBox.hidden) return;
    if (!e.target.closest(".hero-searchbox")) previewBox.hidden = true;
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") previewBox.hidden = true;
  });
}

function renderTopics() {
  const host = document.getElementById("hero-topics");
  if (!host || !input) return;
  host.replaceChildren(
    ...TOPICS.slice(0, 6).map((topic) =>
      el("button", {
        class: "hero-chip",
        type: "button",
        text: topic,
        onclick: () => {
          input.value = topic;
          input.focus();
          handleSearchInput();
        },
      })
    )
  );
}

// ── Gezeitenkurve: Sitzungen pro Tag, nächste 14 Tage ────────────────
const TIDE_W = 560;
const TIDE_H = 220;
const TIDE_BASE = 180; // y der Grundlinie
const TIDE_TOP = 40; // y des höchsten Tages

function svgEl(tag, attrs = {}, text = "") {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (text) node.textContent = text;
  return node;
}

/** Weicher Kurvenzug durch die Punkte (waagrechte Tangenten an jedem Punkt). */
function tidePath(points) {
  const [first, ...rest] = points;
  let d = `M${first.x} ${TIDE_BASE} L${first.x} ${first.y}`;
  let prev = first;
  for (const p of rest) {
    const mid = (prev.x + p.x) / 2;
    d += ` C${mid} ${prev.y} ${mid} ${p.y} ${p.x} ${p.y}`;
    prev = p;
  }
  return `${d} L${prev.x} ${TIDE_BASE} Z`;
}

function weekdayShort(day) {
  return new Date(`${day}T12:00:00Z`)
    .toLocaleDateString("de-DE", { weekday: "short", timeZone: "UTC" })
    .replace(".", "");
}

function dayLabel(day) {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

function renderTide(perDay) {
  const fig = document.getElementById("tide");
  const host = document.getElementById("tide-chart");
  if (!fig || !host || !Array.isArray(perDay) || perDay.length < 2) return;

  const total = perDay.reduce((sum, d) => sum + d.count, 0);
  const max = Math.max(1, ...perDay.map((d) => d.count));
  const step = (TIDE_W - 40) / (perDay.length - 1);
  const points = perDay.map((d, i) => ({
    ...d,
    x: 20 + i * step,
    y: TIDE_BASE - (d.count / max) * (TIDE_BASE - TIDE_TOP),
  }));

  // Für Screenreader: die Tage mit den meisten Sitzungen in Worten.
  const peaks = points
    .filter((p) => p.count === max && p.count > 0)
    .map((p) => dayLabel(p.day));
  const label =
    total === 0
      ? "In den nächsten 14 Tagen ist keine Sitzung bekannt."
      : `${total} Sitzungen in den nächsten 14 Tagen. Am meisten am ${peaks.join(
          " und am "
        )} mit je ${max}.`;

  const svg = svgEl("svg", {
    viewBox: `0 0 ${TIDE_W} ${TIDE_H}`,
    role: "img",
    "aria-label": label,
    class: "tide-svg",
  });
  svg.append(
    svgEl("line", {
      class: "tide-ruler",
      x1: 0,
      y1: TIDE_BASE,
      x2: TIDE_W,
      y2: TIDE_BASE,
    }),
    svgEl("path", { class: "tide-water", d: tidePath(points) })
  );

  const counts = svgEl("g", { class: "tide-count", "aria-hidden": "true" });
  const days = svgEl("g", { class: "tide-day", "aria-hidden": "true" });
  for (const p of points) {
    // Zahl nur dort, wo die Wassersäule hoch genug für die Ziffer ist.
    if (p.count > 0 && TIDE_BASE - p.y >= 30) {
      counts.append(svgEl("text", { x: p.x, y: p.y + 22 }, String(p.count)));
    }
    days.append(
      svgEl("text", { x: p.x, y: TIDE_BASE + 22 }, weekdayShort(p.day))
    );
  }
  const today = svgEl("g", { class: "tide-today", "aria-hidden": "true" });
  today.append(
    svgEl("line", { x1: 20, y1: 20, x2: 20, y2: TIDE_BASE }),
    svgEl("text", { x: 28, y: 30 }, "Heute")
  );
  svg.append(counts, days, today);

  host.replaceChildren(svg);
  const totalNode = document.getElementById("tide-total");
  if (totalNode) {
    totalNode.textContent =
      total === 1 ? "1 Termin" : `${total.toLocaleString("de-DE")} Termine`;
  }
  fig.hidden = false;
}

// ── Abfahrtstafel: die nächsten Sitzungen ───────────────────────────
function hasReliableTime(ev) {
  const start = ev.start ? new Date(ev.start) : null;
  return (
    ev.has_time === 1 ||
    (ev.has_time !== 0 &&
      start &&
      (start.getHours() !== 0 || start.getMinutes() !== 0))
  );
}

function boardRow(ev) {
  const start = ev.start ? new Date(ev.start) : null;
  const href = `/sitzung?id=${encodeURIComponent(ev.external_id)}`;
  const rel = relativeDay(ev.start);
  const day = start
    ? start.toLocaleDateString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      })
    : "";
  const time =
    start && hasReliableTime(ev)
      ? start.toLocaleTimeString("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "offen";
  const title = ev.title || ev.gremium || "Sitzung";
  const sub = [ev.gremium !== title ? ev.gremium : null, ev.kommune]
    .filter(Boolean)
    .join(", ");
  const docs = ev.attachmentCount || 0;

  return el("tr", {}, [
    el("td", { class: "board-time" }, [
      el("span", { class: "board-clock", text: time }),
      el("span", {
        class: "board-day",
        text: rel === "heute" || rel === "morgen" ? `${rel}, ${day}` : day,
      }),
    ]),
    el("td", { class: "board-gremium" }, [
      el("a", { href, text: title }),
      sub && el("span", { text: sub }),
    ]),
    el("td", { class: "board-where", text: ev.location || "" }),
    el("td", { class: "board-state" }, [
      el("span", {
        class: docs > 0 ? "board-pill board-pill--ready" : "board-pill",
        text:
          docs > 0
            ? `${docs} ${docs === 1 ? "Unterlage" : "Unterlagen"}`
            : "nur Termin",
      }),
    ]),
  ]);
}

function renderBoard(meetings) {
  const host = document.getElementById("upcoming-meetings");
  if (!host) return;
  if (!meetings.length) {
    host.replaceChildren(
      el("p", {
        class: "board-empty",
        text: "Gerade ist keine Sitzung angesetzt. Im Kalender findest du vergangene Sitzungen.",
      })
    );
    return;
  }
  host.replaceChildren(
    el("table", { class: "board-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { scope: "col", text: "Wann" }),
          el("th", { scope: "col", text: "Gremium" }),
          el("th", { scope: "col", class: "board-where", text: "Ort" }),
          el("th", { scope: "col", class: "board-state", text: "Unterlagen" }),
        ]),
      ]),
      el("tbody", {}, meetings.map(boardRow)),
    ])
  );
}

// ── Neu eingegangen ─────────────────────────────────────────────────
function renderDocuments(documents) {
  const host = document.getElementById("latest-documents");
  if (!host) return;
  if (!documents.length) {
    host.replaceChildren(
      el("p", { class: "home-empty", text: "Hier ist gerade nichts Neues." })
    );
    return;
  }
  host.replaceChildren(
    el(
      "ul",
      { class: "doc-list" },
      documents.map((doc) =>
        el("li", {}, [
          el("a", {
            href: documentUrl(doc),
            text: contextualDocumentTitle(doc),
          }),
          el("p", {
            text: [
              doc.dokumenttyp || fileLabel(doc),
              doc.Gremium,
              doc.Kommune,
              formatDate(doc.start),
            ]
              .filter(Boolean)
              .join(", "),
          }),
        ])
      )
    )
  );
}

// ── Abdeckung: ein Kästchen je Kommune ──────────────────────────────
function renderCoverage(stats) {
  const host = document.getElementById("coverage-meter");
  const { kommunen, sources } = stats;
  if (
    !host ||
    typeof kommunen !== "number" ||
    typeof sources !== "number" ||
    sources < 1
  )
    return;
  const withDocs = Math.min(kommunen, sources);
  const without = sources - withDocs;
  const boxes = el("div", {
    class: "coverage-boxes",
    role: "img",
    "aria-label": `${withDocs} von ${sources} Kommunen mit Dokumenten`,
    style: `--n: ${sources}`,
  });
  for (let i = 0; i < sources; i += 1) {
    boxes.append(el("i", { class: i < withDocs ? "is-full" : "" }));
  }
  host.replaceChildren(
    boxes,
    el("p", { class: "coverage-legend" }, [
      el("span", { class: "coverage-key coverage-key--full" }),
      `${withDocs} mit Dokumenten`,
      el("span", { class: "coverage-key" }),
      `${without} nur mit Terminen oder ohne Daten`,
    ])
  );
  host.hidden = false;
}

async function loadOverviews() {
  try {
    const data = await fetchHome({ documents: 6, meetings: 6 });
    renderTide(data.meetingsPerDay);
    renderBoard(data.meetings || []);
    renderDocuments(data.documents || []);
    renderCoverage(data.stats || {});
  } catch (err) {
    const host = document.getElementById("upcoming-meetings");
    if (host) {
      host.replaceChildren(
        el("p", {
          class: "board-empty",
          text: `Inhalte konnten nicht geladen werden: ${err.message}`,
        })
      );
    }
  }
}

setupSearch();
renderTopics();
loadOverviews();
